import type { SafelyFetchGraphQlFn } from "@/domains/api/safely-fetch-graphql"
import type { TokenStore } from "@/domains/tokens/token-store"
import {
  manualClIndexerStatusResponseSchema,
  manualClMyPositionsResponseSchema,
  manualClNfpmPositionsResponseSchema,
  manualClPoolActivityResponseSchema,
  manualClPoolOhlcResponseSchema,
  manualClPoolResponseSchema,
  manualClPoolsResponseSchema,
  manualClPoolTicksResponseSchema,
  manualClNfpmPositionHistoryResponseSchema,
  manualClPositionHistoryResponseSchema,
} from "@/domains/validators"

import { THICK_V1_GRAPHQL_ENDPOINT } from "./constants"

/**
 * Thick-v1 GraphQL service. Class name is kept as `ManualClGraphQlService` to
 * preserve call-site compatibility across the migration; the domain boundary
 * is the folder location (`src/domains/thick-v1/`), not the class name.
 *
 * Accepts a fully-qualified `url` (preferred) or a legacy `proxyBaseUrl`
 * which the service itself concatenates with `THICK_V1_GRAPHQL_ENDPOINT`.
 * The dual-mode constructor lets existing call-sites migrate without a
 * coordinated rename.
 */
export class ManualClGraphQlService {
  private readonly baseUrl: string

  private readonly safelyFetchGraphQl: SafelyFetchGraphQlFn

  constructor(params: {
    url?: string
    proxyBaseUrl?: string
    safelyFetchGraphQl: SafelyFetchGraphQlFn
  }) {
    if (params.url) {
      this.baseUrl = params.url
    } else if (params.proxyBaseUrl) {
      this.baseUrl = `${params.proxyBaseUrl}${THICK_V1_GRAPHQL_ENDPOINT}`
    } else {
      throw new Error(
        "ManualClGraphQlService requires either `url` or `proxyBaseUrl` to be set",
      )
    }
    this.safelyFetchGraphQl = params.safelyFetchGraphQl
  }

  getPools = async () => {
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query getPools {
                  Pool(where: { chainId: { _eq: 8453 } }, order_by: {totalValueLockedUSD: desc}) {
                    id
                    address
                    feeTier
                    feesUsd: feesUSD
                    token0 {
                      id
                      address
                      decimals
                      name
                      symbol
                    }
                    token0Price
                    token1 {
                      id
                      address
                      decimals
                      name
                      symbol
                    }
                    token1Price
                    volumeUsd: volumeUSD
                    untrackedVolumeUsd: untrackedVolumeUSD
                    totalValueLockedUsd: totalValueLockedUSD
                    totalValueLockedToken0
                    totalValueLockedToken1
                    liquidityProviderCount
                    liquidity
                    tick
                    sqrtPrice
                    poolDayData(limit: 7, order_by: { date: desc }) {
                      date
                      volumeUSD
                      feesUSD
                      tvlUSD
                      openPrice
                      close
                      high
                      low
                    }
                    poolHourData(limit: 24, order_by: { periodStartUnix: desc }) {
                      periodStartUnix
                      volumeUSD
                      feesUSD
                      openPrice
                      close
                      high
                      low
                    }
                  }
                }`,
        variables: null,
        operationName: "getPools",
      }),
      returnOnError: [],
    })

    const transformedResponse =
      response && typeof response === "object" && "Pool" in response
        ? { pools: (response as { Pool: unknown[] }).Pool }
        : response

    const parsedResponse = manualClPoolsResponseSchema.safeParse(transformedResponse)
    if (!parsedResponse.success) {
      console.error("[ManualClGraphQlService.getPools] Schema validation failed:", parsedResponse.error)
      return []
    }
    return parsedResponse.data.pools
  }

  getPool = async (address: string, chainId: number) => {
    const safeAddress = address ? address.toLowerCase() : ""
    const poolId = `${safeAddress}-${chainId}`
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query getPool {
                  Pool_by_pk(id: "${poolId}") {
                    id
                    address
                    feeTier
                    feesUSD
                    token0 {
                      id
                      address
                      decimals
                      name
                      symbol
                    }
                    token0Price
                    token1 {
                      id
                      address
                      decimals
                      name
                      symbol
                    }
                    tick
                    sqrtPrice
                    token1Price
                    volumeUSD
                    untrackedVolumeUSD
                    totalValueLockedUSD
                    totalValueLockedToken0
                    totalValueLockedToken1
                    liquidityProviderCount
                    liquidity
                    poolDayData(limit: 7, order_by: { date: desc }) {
                      date
                      volumeUSD
                      feesUSD
                      tvlUSD
                      openPrice
                      close
                      high
                      low
                    }
                    poolHourData(limit: 168, order_by: { periodStartUnix: desc }) {
                      periodStartUnix
                      volumeUSD
                      feesUSD
                      openPrice
                      close
                      high
                      low
                    }
                  }
                }`,
        variables: null,
        operationName: "getPool",
      }),
      returnOnError: [],
    })

    const parsedResponse = manualClPoolResponseSchema.safeParse(response)

    if (!parsedResponse.success || !parsedResponse.data.Pool_by_pk) {
      return null
    }
    return parsedResponse.data.Pool_by_pk
  }

  getPoolTicks = async (address: string, chainId: number) => {
    // `Tick.poolAddress` in the Envio HyperIndex schema is the composite pool
    // ID (`<address>-<chainId>`), not the raw EVM address — matching the
    // convention `Pool_by_pk` uses for its `id` column. Filtering on the raw
    // address silently returns an empty set and manifests as "Liquidity · No
    // data" in the range-brush; always append the chain suffix here.
    const safeAddress = address ? address.toLowerCase() : ""
    const safePoolId = safeAddress ? `${safeAddress}-${chainId}` : ""
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query getPoolTicks {
                  Tick(
                    where: {
                      _and: [
                        { poolAddress: { _eq: "${safePoolId}" } }
                        { liquidityGross: { _gt: "0" } }
                      ]
                    }
                    order_by: { tickIdx: asc }
                  ) {
                    id
                    poolAddress
                    tickIdx
                    chainId
                    pool {
                      id
                      address
                    }
                    liquidityGross
                    liquidityNet
                    price0
                    price1
                    createdAtTimestamp
                    createdAtBlockNumber
                  }
                }`,
        variables: null,
        operationName: "getPoolTicks",
      }),
      returnOnError: [],
    })
    const parsedResponse = manualClPoolTicksResponseSchema.safeParse(response)

    if (!parsedResponse.success) {
      return null
    }
    return parsedResponse.data.ticks
  }

  getMyPositions = async (address: string, chainId: number) => {
    const safeAddress = address ? address.toLowerCase() : ""
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query getMyPositions {
                  Position(where: { owner: { _eq: "${safeAddress}" }, chainId: { _eq: ${chainId} } }) {
                    id
                    chainId
                    owner
                    nfpmTokenIds
                    tickLower
                    tickUpper
                    liquidity
                    hasLiquidity
                    totalDepositedToken0
                    totalDepositedToken1
                    totalWithdrawnToken0
                    totalWithdrawnToken1
                    totalCollectedToken0
                    totalCollectedToken1
                    totalCollectedUSD
                    createdAtTimestamp
                    createdAtBlockNumber
                    lastActivityTimestamp
                    lastActivityBlockNumber
                    token0 {
                      id
                      address
                      decimals
                      derivedETH
                      name
                      symbol
                    }
                    token1 {
                      id
                      address
                      decimals
                      derivedETH
                      name
                      symbol
                    }
                    pool {
                      id
                      address
                      feeTier
                      feesUSD
                      token0Price
                      token1Price
                      volumeUSD
                      untrackedVolumeUSD
                      totalValueLockedUSD
                      totalValueLockedToken0
                      totalValueLockedToken1
                      liquidityProviderCount
                      liquidity
                      tick
                      sqrtPrice
                    }
                  }
                  Bundle_by_pk(id: "${chainId}") {
                    ethPriceUSD
                  }
                }`,
        operationName: "getMyPositions",
      }),
      returnOnError: [],
    })
    const parsedResponse = manualClMyPositionsResponseSchema.safeParse(response)

    if (!parsedResponse.success) {
      return null
    }
    return parsedResponse.data
  }

  /**
   * Indexer-native per-NFT rows for the connected wallet (P7). Each row is
   * the authoritative source for:
   *   - real NFPM tokenId (drops the on-chain enumeration loop)
   *   - principal0/1 underlying amounts (drops the simulated decreaseLiquidity)
   *   - lifetime deposited/withdrawn/collected raw-wei totals
   *
   * Live pending fees (tokensOwed0/1) are NOT in this feed — they remain a
   * render-time RPC (`nfpmUserStats`). See the P7 comment in `schema.graphql`.
   */
  getMyNfpmPositions = async (address: string, chainId: number) => {
    const safeAddress = address ? address.toLowerCase() : ""
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query getMyNfpmPositions {
                  NfpmPosition(where: { owner: { _eq: "${safeAddress}" }, chainId: { _eq: ${chainId} } }) {
                    id
                    chainId
                    nfpmAddress
                    tokenId
                    owner
                    originalMinter
                    tickLower
                    tickUpper
                    liquidity
                    principal0
                    principal1
                    totalDeposited0
                    totalDeposited1
                    totalWithdrawn0
                    totalWithdrawn1
                    totalCollected0
                    totalCollected1
                    closed
                    createdAtTimestamp
                    createdAtBlockNumber
                    lastEventTimestamp
                    lastEventBlockNumber
                    pool { id address feeTier }
                    token0 { id address decimals symbol }
                    token1 { id address decimals symbol }
                  }
                }`,
        operationName: "getMyNfpmPositions",
      }),
      returnOnError: [],
    })
    const parsed = manualClNfpmPositionsResponseSchema.safeParse(response)
    if (!parsed.success) {
      console.error(
        "[ManualClGraphQlService.getMyNfpmPositions] Schema validation failed:",
        parsed.error,
      )
      return { nfpmPositions: [] }
    }
    return parsed.data
  }

  /**
   * TokenId-specific lifecycle from the indexer's per-NFT NFPM tables. Closed
   * NFTs are pruned from range-level `Position.nfpmTokenIds`, so history routes
   * must be able to read the retained `NfpmPosition` row directly.
   */
  getNfpmPositionHistory = async (nfpmPositionId: string) => {
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query getNfpmPositionHistory {
          NfpmPosition_by_pk(id: "${nfpmPositionId}") {
            id
            token0 { decimals symbol }
            token1 { decimals symbol }
            increases(order_by: { timestamp: desc }) {
              id
              timestamp
              amount0
              amount1
              transactionHash
              logIndex
            }
            decreases(order_by: { timestamp: desc }) {
              id
              timestamp
              amount0
              amount1
              transactionHash
              logIndex
            }
            collects(order_by: { timestamp: desc }) {
              id
              timestamp
              amount0
              amount1
              transactionHash
              logIndex
            }
          }
        }`,
        operationName: "getNfpmPositionHistory",
      }),
      returnOnError: [],
    })

    const parsed = manualClNfpmPositionHistoryResponseSchema.safeParse(response)
    if (!parsed.success) {
      console.error(
        "[ManualClGraphQlService.getNfpmPositionHistory] Schema validation failed:",
        parsed.error,
      )
      return { positionId: undefined, history: [] }
    }
    return parsed.data
  }

  getRelatedTokenPools = async (token0: TokenStore, token1: TokenStore) => {
    const token0Id = token0.tokenId
    const token1Id = token1.tokenId

    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query getRelatedTokenPools {
                  Pool(
                    where: {
                      _or: [
                        {
                          token0_id: { _eq: "${token0Id}" },
                          token1_id: { _eq: "${token1Id}" }
                        },
                        {
                          token0_id: { _eq: "${token1Id}" },
                          token1_id: { _eq: "${token0Id}" }
                        }
                      ]
                    },
                    limit: 100
                  ) {
                    id
                    address
                    feeTier
                    feesUsd: feesUSD
                    token0_id
                    token0 {
                      id
                      address
                      decimals
                      name
                      symbol
                    }
                    token0Price
                    token1_id
                    token1 {
                      id
                      address
                      decimals
                      name
                      symbol
                    }
                    token1Price
                    volumeUsd: volumeUSD
                    untrackedVolumeUsd: untrackedVolumeUSD
                    totalValueLockedUsd: totalValueLockedUSD
                    totalValueLockedToken0
                    totalValueLockedToken1
                    liquidityProviderCount
                  }
                }`,
      }),
      returnOnError: [],
    })

    const transformedResponse =
      response && typeof response === "object" && "Pool" in response
        ? { pools: (response as { Pool: unknown[] }).Pool }
        : { pools: [] }

    const parsedResponse = manualClPoolsResponseSchema.safeParse(transformedResponse)

    if (!parsedResponse.success) {
      console.error(
        "[ManualClGraphQlService.getRelatedTokenPools] Schema validation failed:",
        parsedResponse.error,
      )
      return []
    }
    return parsedResponse.data.pools
  }

  /**
   * Paginated Swap / Mint / Burn / Collect feed for a single pool. Rows are
   * ordered newest-first per event type; consumers interleave them by
   * `timestamp` for a unified feed.
   */
  getPoolActivity = async (poolAddress: string, limit: number = 25, offset: number = 0) => {
    const safeAddress = poolAddress ? poolAddress.toLowerCase() : ""
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query getPoolActivity {
          Swap(
            where: { pool: { address: { _eq: "${safeAddress}" } }, chainId: { _eq: 8453 } }
            order_by: { timestamp: desc }
            limit: ${limit}
            offset: ${offset}
          ) {
            id
            timestamp
            amount0
            amount1
            amountUSD
            origin
            sender
            transaction { id transactionHash }
            logIndex
          }
          Mint(
            where: { pool: { address: { _eq: "${safeAddress}" } }, chainId: { _eq: 8453 } }
            order_by: { timestamp: desc }
            limit: ${limit}
            offset: ${offset}
          ) {
            id
            timestamp
            amount0
            amount1
            amountUSD
            origin
            owner
            sender
            transaction { id transactionHash }
            logIndex
          }
          Burn(
            where: { pool: { address: { _eq: "${safeAddress}" } }, chainId: { _eq: 8453 } }
            order_by: { timestamp: desc }
            limit: ${limit}
            offset: ${offset}
          ) {
            id
            timestamp
            amount0
            amount1
            amountUSD
            origin
            owner
            transaction { id transactionHash }
            logIndex
          }
          Collect(
            where: { pool: { address: { _eq: "${safeAddress}" } }, chainId: { _eq: 8453 } }
            order_by: { timestamp: desc }
            limit: ${limit}
            offset: ${offset}
          ) {
            id
            timestamp
            amount0
            amount1
            amountUSD
            owner
            transaction { id transactionHash }
            logIndex
          }
        }`,
        operationName: "getPoolActivity",
      }),
      returnOnError: [],
    })

    const parsed = manualClPoolActivityResponseSchema.safeParse(response)
    if (!parsed.success) {
      console.error("[ManualClGraphQlService.getPoolActivity] Schema validation failed:", parsed.error)
      return { swaps: [], mints: [], burns: [], collects: [] }
    }
    return parsed.data
  }

  /**
   * Pool hourly OHLC plus volume/fees/tvl for chart rendering. Results are
   * ascending so charts can read straight through.
   */
  getPoolOHLC = async (poolAddress: string, sinceUnix: number) => {
    const safeAddress = poolAddress ? poolAddress.toLowerCase() : ""
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query getPoolOHLC {
          PoolHourData(
            where: {
              pool: { address: { _eq: "${safeAddress}" } }
              chainId: { _eq: 8453 }
              periodStartUnix: { _gte: ${Math.floor(sinceUnix)} }
            }
            order_by: { periodStartUnix: asc }
          ) {
            periodStartUnix
            openPrice
            close
            high
            low
            volumeUSD
            feesUSD
            tvlUSD
          }
        }`,
        operationName: "getPoolOHLC",
      }),
      returnOnError: [],
    })

    const parsed = manualClPoolOhlcResponseSchema.safeParse(response)
    if (!parsed.success) {
      console.error("[ManualClGraphQlService.getPoolOHLC] Schema validation failed:", parsed.error)
      return { points: [] }
    }
    return parsed.data
  }

  /**
   * Full lifecycle of a position — every Mint, Burn and Collect attributed
   * to the given Position.id. Consumers merge and sort by timestamp.
   */
  getPositionHistory = async (positionId: string) => {
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query getPositionHistory {
          Position_by_pk(id: "${positionId}") {
            id
            mints(order_by: { timestamp: desc }) {
              id
              timestamp
              amount0
              amount1
              amountUSD
              transaction { id transactionHash }
              logIndex
            }
            burns(order_by: { timestamp: desc }) {
              id
              timestamp
              amount0
              amount1
              amountUSD
              transaction { id transactionHash }
              logIndex
            }
            collects(order_by: { timestamp: desc }) {
              id
              timestamp
              amount0
              amount1
              amountUSD
              transaction { id transactionHash }
              logIndex
            }
          }
        }`,
        operationName: "getPositionHistory",
      }),
      returnOnError: [],
    })

    const parsed = manualClPositionHistoryResponseSchema.safeParse(response)
    if (!parsed.success) {
      console.error(
        "[ManualClGraphQlService.getPositionHistory] Schema validation failed:",
        parsed.error,
      )
      return { positionId: undefined, history: [] }
    }
    return parsed.data
  }

  /**
   * Indexer freshness, sourced from Envio's built-in `chain_metadata` table
   * rather than our custom `IndexerStatus` entity.
   *
   * The custom entity (Proposal 6) only advances when an event handler
   * fires, so on chains / time windows where no Manual-CL events land it
   * goes stale even though the indexer itself is fully caught up to head.
   * That produced the "Indexer significantly behind head · 16m behind"
   * banner users saw while the indexer was actually healthy.
   *
   * `chain_metadata` is maintained by Envio as HyperSync advances, so:
   *   - `block_height` = the chain tip as known to the indexer
   *   - `latest_processed_block` (progressBlock) = committed to DB, safe
   *     for the UI to read
   *   - `latest_fetched_block_number` (bufferBlock) = fetched into
   *     HyperSync but not necessarily in the DB yet
   *
   * We compare `block_height` against `latest_processed_block` — i.e.
   * progressBlock, not bufferBlock — because users only care about "how
   * behind is what I can actually read right now". The delta is expressed
   * in blocks, which avoids the wall-clock skew problem the previous
   * implementation hit.
   */
  getIndexerStatus = async (chainId: number = 8453) => {
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query getIndexerStatus {
          chain_metadata(where: { chain_id: { _eq: ${chainId} } }, limit: 1) {
            chain_id
            block_height
            latest_processed_block
            latest_fetched_block_number
            timestamp_caught_up_to_head_or_endblock
          }
        }`,
        operationName: "getIndexerStatus",
      }),
      returnOnError: [],
    })

    const parsed = manualClIndexerStatusResponseSchema.safeParse(response)
    if (!parsed.success) {
      // `chain_metadata` is a built-in Envio table; a schema miss here
      // means the endpoint is unreachable or the indexer shape changed,
      // not an expected steady state. Surface null so the UI hides the
      // banner rather than displaying garbage, and let the console
      // warning already emitted by `safelyFetchGraphQl` flag it.
      return null
    }
    return parsed.data
  }
}
